import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import ImageModal from "../components/ui/ImageModal";
import Footer from "../components/home/Footer";
import DynamicBackground from "../components/home/DynamicBackground";
import { translations } from "../components/home/translations";
import { 
  Shield, 
  Users, 
  Settings, 
  Target, 
  Bot, 
  Lock, 
  TrendingUp, 
  Play, 
  Mail,
  CheckCircle,
  BarChart3,
  Zap,
  Brain,
  Eye,
  Download,
  ArrowLeft,
  Globe
} from "lucide-react";

export default function TSEG() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('hero');
  const [modalImage, setModalImage] = useState({ src: '', alt: '', isOpen: false });
  const [language, setLanguage] = useState('en');
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langRef = useRef(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Handle language dropdown click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langRef.current && !langRef.current.contains(event.target)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['hero', 'value-props', 'safety', 'users', 'admins', 'campaigns', 'ai', 'security', 'outcomes', 'footer-cta'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleRequestDemo = () => {
    // Navigate to homepage and scroll to contact section
    navigate('/');
    setTimeout(() => {
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const handleContactSales = () => {
    // Navigate to homepage and scroll to contact section
    navigate('/');
    setTimeout(() => {
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const openImageModal = (src, alt) => {
    setModalImage({ src, alt, isOpen: true });
  };

  const closeImageModal = () => {
    setModalImage({ src: '', alt: '', isOpen: false });
  };

  const navItems = [
    { id: 'hero', label: 'Home' },
    { id: 'value-props', label: 'Value' },
    { id: 'safety', label: 'Safety' },
    { id: 'users', label: 'Users' },
    { id: 'admins', label: 'Admins' },
    { id: 'campaigns', label: 'Campaigns' },
    { id: 'ai', label: 'AI' },
    { id: 'security', label: 'Security' },
    { id: 'outcomes', label: 'Results' }
  ];

  const texts = translations[language];

  return (
    <div className="bg-black text-white min-h-screen relative">
      {/* Custom TSEG Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-lg border-b border-gray-800/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Back to Home Button */}
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>{texts.tseg.backToHome}</span>
            </Button>

            {/* Centered Logo and Text */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center space-x-3 absolute left-1/2 transform -translate-x-1/2"
            >
              <img 
                src={require("../assets/Logo_TSEG.png")} 
                alt="TSEG Logo" 
                className="w-16 h-16 object-contain"
              />
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                TSEG
              </span>
            </motion.div>

            {/* Language Switcher */}
            <div className="flex items-center space-x-4">
              <div className="relative" ref={langRef}>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-gray-300 hover:text-white"
                  onClick={() => setIsLangOpen((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={isLangOpen}
                  aria-label="Change language"
                >
                  <Globe className="h-5 w-5" />
                </Button>
                {isLangOpen && (
                  <div className="absolute right-0 mt-2 w-56 origin-top-right rounded-md bg-gray-900 border border-gray-700 text-white shadow-lg ring-1 ring-black/50 focus:outline-none z-50">
                    <div className="py-1">
                      <button 
                        onClick={() => {
                          setLanguage('en');
                          setIsLangOpen(false);
                        }} 
                        className="block w-full px-4 py-2 text-sm text-left hover:bg-gray-800"
                      >
                        EN (English)
                      </button>
                      <button 
                        onClick={() => {
                          setLanguage('ro');
                          setIsLangOpen(false);
                        }} 
                        className="block w-full px-4 py-2 text-sm text-left hover:bg-gray-800"
                      >
                        RO (Română)
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>
      
      
      <main className="relative z-10">
        {/* Hero Section */}
        <section id="hero" className="min-h-screen flex items-center justify-center px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <h1 className="text-5xl md:text-7xl font-bold bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent leading-tight">
                {texts.tseg.heroTitle}
              </h1>
              
              <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
                {texts.tseg.heroSubtitle}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button 
                  size="lg"
                  onClick={handleRequestDemo}
                  className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-8 py-4 text-lg rounded-xl transition-all duration-300 transform hover:scale-105"
                >
                  <Play className="mr-2 h-5 w-5" />
                  {texts.tseg.requestDemo}
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  onClick={handleContactSales}
                  className="border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-black px-8 py-4 text-lg rounded-xl transition-all duration-300"
                >
                  <Mail className="mr-2 h-5 w-5" />
                  {texts.tseg.contactSales}
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Value Props Section */}
        <section id="value-props" className="py-20 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="grid md:grid-cols-3 gap-8"
            >
              <Card className="bg-gray-900/50 border-gray-800 hover:border-blue-500/50 transition-all duration-300 hover:transform hover:scale-105">
                <CardHeader className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Target className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-2xl text-white">{texts.tseg.valueProps.card1Title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-300 text-lg text-center">
                    {texts.tseg.valueProps.card1Desc}
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="bg-gray-900/50 border-gray-800 hover:border-blue-500/50 transition-all duration-300 hover:transform hover:scale-105">
                <CardHeader className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Settings className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-2xl text-white">{texts.tseg.valueProps.card2Title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-300 text-lg text-center">
                    {texts.tseg.valueProps.card2Desc}
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="bg-gray-900/50 border-gray-800 hover:border-blue-500/50 transition-all duration-300 hover:transform hover:scale-105">
                <CardHeader className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <BarChart3 className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-2xl text-white">{texts.tseg.valueProps.card3Title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-300 text-lg text-center">
                    {texts.tseg.valueProps.card3Desc}
                  </CardDescription>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* Safety Overview Section */}
        <section id="safety" className="py-20 px-6 lg:px-8 bg-gradient-to-r from-gray-900/30 to-gray-800/30">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center space-y-8"
            >
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-3xl flex items-center justify-center mx-auto">
                <Shield className="h-10 w-10 text-white" />
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                {texts.tseg.safety.title}
              </h2>
              <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
                {texts.tseg.safety.subtitle}
              </p>
              <div className="mt-12 grid md:grid-cols-2 gap-12 max-w-7xl mx-auto">
                <div className="relative cursor-pointer group" onClick={() => openImageModal(require("../assets/Statistics1_Eng.png"), "Security statistics dashboard")}>
                  <img 
                    src={require("../assets/Statistics1_Eng.png")} 
                    alt="Security statistics dashboard" 
                    className="rounded-2xl shadow-2xl w-full h-auto min-h-[400px] object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute -top-4 -right-4 bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full p-4">
                    <BarChart3 className="h-8 w-8 text-white" />
                  </div>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 rounded-2xl flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/20 backdrop-blur-sm rounded-full p-3">
                      <Eye className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>
                <div className="relative cursor-pointer group" onClick={() => openImageModal(require("../assets/Statistics4_Eng.png"), "Advanced analytics dashboard")}>
                  <img 
                    src={require("../assets/Statistics4_Eng.png")} 
                    alt="Advanced analytics dashboard" 
                    className="rounded-2xl shadow-2xl w-full h-auto min-h-[400px] object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute -bottom-4 -left-4 bg-gradient-to-r from-cyan-500 to-blue-400 rounded-full p-4">
                    <TrendingUp className="h-8 w-8 text-white" />
                  </div>
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 rounded-2xl flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/20 backdrop-blur-sm rounded-full p-3">
                      <Eye className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* For Users Section */}
        <section id="users" className="py-20 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="grid lg:grid-cols-2 gap-16 items-center"
            >
              <div className="space-y-8">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-2xl flex items-center justify-center">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-white">
                  {texts.tseg.users.title}
                </h2>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <CheckCircle className="h-6 w-6 text-green-400 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">{texts.tseg.users.feature1Title}</h3>
                      <p className="text-gray-300">{texts.tseg.users.feature1Desc}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <CheckCircle className="h-6 w-6 text-green-400 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">{texts.tseg.users.feature2Title}</h3>
                      <p className="text-gray-300">{texts.tseg.users.feature2Desc}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <CheckCircle className="h-6 w-6 text-green-400 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">{texts.tseg.users.feature3Title}</h3>
                      <p className="text-gray-300">{texts.tseg.users.feature3Desc}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-6">
                  <p className="text-lg text-blue-200 font-medium">
                    {texts.tseg.users.highlight}
                  </p>
                </div>
              </div>
              <div className="relative w-full cursor-pointer group" onClick={() => openImageModal(require("../assets/UserHome_Eng.png"), "User learning interface showing courses, tests and news")}>
                <img 
                  src={require("../assets/UserHome_Eng.png")} 
                  alt="User learning interface showing courses, tests and news" 
                  className="rounded-2xl shadow-2xl w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute -top-4 -right-4 bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full p-4">
                  <Zap className="h-8 w-8 text-white" />
                </div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 rounded-2xl flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/20 backdrop-blur-sm rounded-full p-3">
                    <Eye className="h-6 w-6 text-white" />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* For Admins Section */}
        <section id="admins" className="py-20 px-6 lg:px-8 bg-gradient-to-r from-gray-900/30 to-gray-800/30">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center space-y-12"
            >
              <div className="space-y-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-2xl flex items-center justify-center mx-auto">
                  <Settings className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-white">
                  {texts.tseg.admins.title}
                </h2>
              </div>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card className="bg-gray-900/50 border-gray-800 hover:border-blue-500/50 transition-all duration-300">
                  <CardHeader className="text-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <Eye className="h-6 w-6 text-white" />
                    </div>
                    <CardTitle className="text-lg text-white">{texts.tseg.admins.card1Title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-300 text-sm text-center">
                      {texts.tseg.admins.card1Desc}
                    </CardDescription>
                  </CardContent>
                </Card>

                <Card className="bg-gray-900/50 border-gray-800 hover:border-blue-500/50 transition-all duration-300">
                  <CardHeader className="text-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <Users className="h-6 w-6 text-white" />
                    </div>
                    <CardTitle className="text-lg text-white">{texts.tseg.admins.card2Title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-300 text-sm text-center">
                      {texts.tseg.admins.card2Desc}
                    </CardDescription>
                  </CardContent>
                </Card>

                <Card className="bg-gray-900/50 border-gray-800 hover:border-blue-500/50 transition-all duration-300">
                  <CardHeader className="text-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <Download className="h-6 w-6 text-white" />
                    </div>
                    <CardTitle className="text-lg text-white">{texts.tseg.admins.card3Title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-300 text-sm text-center">
                      {texts.tseg.admins.card3Desc}
                    </CardDescription>
                  </CardContent>
                </Card>

                <Card className="bg-gray-900/50 border-gray-800 hover:border-blue-500/50 transition-all duration-300">
                  <CardHeader className="text-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <Mail className="h-6 w-6 text-white" />
                    </div>
                    <CardTitle className="text-lg text-white">{texts.tseg.admins.card4Title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-300 text-sm text-center">
                      {texts.tseg.admins.card4Desc}
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Campaigns Section */}
        <section id="campaigns" className="py-20 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center space-y-12"
            >
              <div className="space-y-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-2xl flex items-center justify-center mx-auto">
                  <Target className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-white">
                  {texts.tseg.campaigns.title}
                </h2>
              </div>
              
              <div className="grid md:grid-cols-2 gap-8">
                <Card className="bg-gradient-to-br from-blue-900/20 to-cyan-900/20 border-blue-500/30 hover:border-blue-400/50 transition-all duration-300">
                  <CardHeader className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Zap className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-2xl text-white">{texts.tseg.campaigns.customTitle}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-300 text-lg text-center">
                      {texts.tseg.campaigns.customDesc}
                    </CardDescription>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-cyan-900/20 to-blue-900/20 border-cyan-500/30 hover:border-cyan-400/50 transition-all duration-300">
                  <CardHeader className="text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-400 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Settings className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-2xl text-white">{texts.tseg.campaigns.automatedTitle}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-300 text-lg text-center">
                      {texts.tseg.campaigns.automatedDesc}
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>
            </motion.div>
          </div>
        </section>

        {/* AI & Smart Matching Section */}
        <section id="ai" className="py-20 px-6 lg:px-8 bg-gradient-to-r from-gray-900/30 to-gray-800/30">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="grid lg:grid-cols-2 gap-12 items-center"
            >
              <div className="space-y-8">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-2xl flex items-center justify-center">
                  <Brain className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-white">
                  {texts.tseg.ai.title}
                </h2>
                <p className="text-xl text-gray-300 leading-relaxed">
                  {texts.tseg.ai.subtitle}
                </p>
                <div className="flex flex-wrap gap-3">
                  <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30 px-4 py-2">
                    {texts.tseg.ai.badge1}
                  </Badge>
                  <Badge className="bg-cyan-500/20 text-cyan-300 border-cyan-500/30 px-4 py-2">
                    {texts.tseg.ai.badge2}
                  </Badge>
                  <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30 px-4 py-2">
                    {texts.tseg.ai.badge3}
                  </Badge>
                </div>
              </div>
              <div className="relative cursor-pointer group" onClick={() => openImageModal(require("../assets/AiGeneration.png"), "AI generation interface showing template creation and matching")}>
                <img 
                  src={require("../assets/AiGeneration.png")} 
                  alt="AI generation interface showing template creation and matching" 
                  className="rounded-2xl shadow-2xl w-full h-auto object-contain group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute -bottom-4 -left-4 bg-gradient-to-r from-blue-500 to-cyan-400 rounded-full p-4">
                  <Bot className="h-8 w-8 text-white" />
                </div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 rounded-2xl flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/20 backdrop-blur-sm rounded-full p-3">
                    <Eye className="h-6 w-6 text-white" />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Security, Privacy & Compliance Section */}
        <section id="security" className="py-20 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center space-y-8"
            >
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-3xl flex items-center justify-center mx-auto">
                <Lock className="h-10 w-10 text-white" />
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white">
                {texts.tseg.security.title}
              </h2>
              <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
                {texts.tseg.security.subtitle}
              </p>
              <div className="grid md:grid-cols-3 gap-6 mt-12">
                <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                  <Shield className="h-8 w-8 text-green-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">{texts.tseg.security.card1Title}</h3>
                  <p className="text-gray-300 text-sm">{texts.tseg.security.card1Desc}</p>
                </div>
                <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                  <Lock className="h-8 w-8 text-green-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">{texts.tseg.security.card2Title}</h3>
                  <p className="text-gray-300 text-sm">{texts.tseg.security.card2Desc}</p>
                </div>
                <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                  <Download className="h-8 w-8 text-green-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">{texts.tseg.security.card3Title}</h3>
                  <p className="text-gray-300 text-sm">{texts.tseg.security.card3Desc}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Outcomes Section */}
        <section id="outcomes" className="py-20 px-6 lg:px-8 bg-gradient-to-r from-gray-900/30 to-gray-800/30">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center space-y-12"
            >
              <div className="space-y-6">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-2xl flex items-center justify-center mx-auto">
                  <TrendingUp className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-4xl md:text-5xl font-bold text-white">
                  {texts.tseg.outcomes.title}
                </h2>
                <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                  {texts.tseg.outcomes.subtitle}
                </p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-gradient-to-br from-green-900/20 to-emerald-900/20 border-green-500/30 rounded-2xl p-8 text-center">
                  <div className="text-4xl font-bold text-green-400 mb-2">{texts.tseg.outcomes.metric1}</div>
                  <h3 className="text-xl font-semibold text-white mb-2">{texts.tseg.outcomes.metric1Title}</h3>
                  <p className="text-gray-300">{texts.tseg.outcomes.metric1Desc}</p>
                </div>
                <div className="bg-gradient-to-br from-blue-900/20 to-cyan-900/20 border-blue-500/30 rounded-2xl p-8 text-center">
                  <div className="text-4xl font-bold text-blue-400 mb-2">{texts.tseg.outcomes.metric2}</div>
                  <h3 className="text-xl font-semibold text-white mb-2">{texts.tseg.outcomes.metric2Title}</h3>
                  <p className="text-gray-300">{texts.tseg.outcomes.metric2Desc}</p>
                </div>
                <div className="bg-gradient-to-br from-purple-900/20 to-pink-900/20 border-purple-500/30 rounded-2xl p-8 text-center">
                  <div className="text-4xl font-bold text-purple-400 mb-2">{texts.tseg.outcomes.metric3}</div>
                  <h3 className="text-xl font-semibold text-white mb-2">{texts.tseg.outcomes.metric3Title}</h3>
                  <p className="text-gray-300">{texts.tseg.outcomes.metric3Desc}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Footer CTA Section */}
        <section id="footer-cta" className="py-20 px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center space-y-8"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-white">
                {texts.tseg.footerCta.title}
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                {texts.tseg.footerCta.subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button 
                  size="lg"
                  onClick={handleRequestDemo}
                  className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-8 py-4 text-lg rounded-xl transition-all duration-300 transform hover:scale-105"
                >
                  <Play className="mr-2 h-5 w-5" />
                  {texts.tseg.requestDemo}
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  onClick={handleContactSales}
                  className="border-2 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-black px-8 py-4 text-lg rounded-xl transition-all duration-300"
                >
                  <Mail className="mr-2 h-5 w-5" />
                  {texts.tseg.contactSales}
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer texts={texts.footer} />

      {/* Enhanced Background elements */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <DynamicBackground />
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/80 to-black/90" />
        <div className="absolute top-0 left-1/3 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/3 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
      </div>

      {/* Image Modal */}
      <ImageModal
        isOpen={modalImage.isOpen}
        onClose={closeImageModal}
        imageSrc={modalImage.src}
        imageAlt={modalImage.alt}
      />
    </div>
  );
}
